import { lazy } from 'react';
import { useSelector } from 'react-redux';
import {
  Navigate,
  // Outlet,
  Route, useLocation,
  Routes,
} from 'react-router-dom';

import { IRootState } from '@app/store';
import sanitizeReturnTo from '@shared/helpers/navigation';
import DashboardLayout from '@shared/layout/dashboard/DashboardLayout';

import ClinicRouter from './clinic/ClinicRouter';
import DentistRouter from './dentist/DentistRouter';
import LaboratoryRouter from './laboratory/LaboratoryRouter';

const ReferralMessages = lazy(() => import('@shared/layout/dashboard/components/messages/ReferralMessages'));
const LabOrderMessages = lazy(() => import('@shared/layout/dashboard/components/messages/lab-order/LabOrderMessages'));
const NotificationSettings = lazy(() => import('@shared/layout/dashboard/components/notification-settings/NotificationSettings'));
const TutorialVideosRouter = lazy(() => import('@shared/layout/dashboard/components/tutorial-videos/TutorialVideosRouter'));
const PackagesPricing = lazy(() => import('@shared/layout/dashboard/components/packages-pricing/PackagesPricing'));
const PlansPayments = lazy(() => import('@shared/layout/dashboard/components/plans-payments/PlansPayments'));

// Define a type for user type list
type UserTypeList = {
  dentist: string;
  'dental-clinic': string;
  laboratory: string;
};

// Define UserType with a union of keys of UserTypeList
type UserType = keyof UserTypeList;

const CustomRouters = {
  dentist: <DentistRouter />,
  'dental-clinic': <ClinicRouter />,
  laboratory: <LaboratoryRouter />,
};

const ClientRouter = () => {
  // const isLoggedIn = useSelector((state: IRootState) => !!state.Auth.accessToken);
  const isLoggedIn = useSelector(({ Auth }) => !!Auth.user.id);
  const userType = useSelector((state: IRootState) => state.Auth?.user?.type) as UserType;
  const isFromLogoutButton = useSelector((state: IRootState) => state.Auth?.isFromLogoutButton);
  const isEmailVerified = useSelector((state: IRootState) => state.Auth.user.is_email_verified);
  const isMobileVerified = useSelector((state: IRootState) => state.Auth.user.is_mobile_verified);
  const location = useLocation();
  const params = new URLSearchParams(location?.state?.from?.search);
  // `||` (not `??`) so an empty-string returnTo also falls back to '/'.
  const returnTo = params.get('returnTo') || '/';
  // Encode the full path + query so a deep link that carries its own query
  // string isn't truncated at the second "?" when the login page reads it
  // back via URLSearchParams — mirrors the 401 interceptor in AxiosConfig.
  const defaultUrl = location.pathname !== '/auth/login' && location.pathname !== '/' ? `/auth/login?returnTo=${encodeURIComponent(location.pathname + location.search)}` : '/auth/login';

  // can only be redirected to a specific page if the user type is equal to the last's user type
  // to avoid page error display
  if (isLoggedIn && (returnTo !== '/' && returnTo !== '/auth/login' && location?.pathname !== returnTo)) {
    return <Navigate to={returnTo} />;
  }

  if (!isLoggedIn) {
    // set last page if the user redirects to a specific page if not yet logged in
    localStorage.setItem('lastPage', sanitizeReturnTo(location.pathname));
    return <Navigate to={isFromLogoutButton ? '/auth/login' : defaultUrl} state={{ from: location }} />;
  }

  if (isLoggedIn && (!isEmailVerified || !isMobileVerified)) {
    return <Navigate to="/verify" />;
  }

  if (!CustomRouters[userType]) {
    // Redirect to page 500 if userType is not found
    return <Navigate to="/errors/500" />;
  }

  return (
    <>
      {/* Private Routes */}
      <Routes>
        {userType !== 'laboratory' && (
          <Route element={<DashboardLayout />}>
            <Route path="packages-pricing" element={<PackagesPricing />} />
            <Route path="plans-payments" element={<PlansPayments />} />
          </Route>
        )}
        <Route element={<DashboardLayout />}>
          <Route path="notification-settings" element={<NotificationSettings />} />
          <Route path="tutorials/*" element={<TutorialVideosRouter />} />
        </Route>
        <Route path="referral-messages/:id" element={<ReferralMessages />} />
        <Route path="lab-order-messages/:id" element={<LabOrderMessages />} />
        <Route element={<DashboardLayout />}>
          <Route path="*" element={CustomRouters[userType]} />
        </Route>
      </Routes>
    </>
  );
};

ClientRouter.propTypes = {

};

export default ClientRouter;
